import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, User, ArrowRight, Loader2, CheckCircle2, AlertCircle, ArrowLeft, Eye, EyeOff, KeyRound, Sparkles } from 'lucide-react';
import { WealthyLogo } from '../components/WealthyLogo';

const Login: React.FC = () => {
  const { login, signup, resetPasswordForEmail, updatePassword, isRecoveryMode, setIsRecoveryMode, logout } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [form, setForm] = useState({
    email: '',
    password: '',
    name: ''
  });

  // Pre-fill email from localStorage or default
  useEffect(() => {
    const savedEmail = localStorage.getItem('wealth_last_email');
    if (savedEmail) {
      setForm(prev => ({ ...prev, email: savedEmail }));
    } else {
      setForm(prev => ({ ...prev, email: 'alexcalanni@gmail.com' }));
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMsg('');

    const cleanEmail = form.email.trim().toLowerCase();
    const cleanPassword = form.password;

    if (!cleanEmail) {
      setError('Please provide a valid network address.');
      setLoading(false);
      return;
    }

    if (!cleanPassword) {
      setError('Please enter your access key.');
      setLoading(false);
      return;
    }

    // Save email for user convenience
    localStorage.setItem('wealth_last_email', cleanEmail);

    try {
      if (isLogin) {
        const result = await login(cleanEmail, cleanPassword);
        if (!result.success) {
          setError(result.error || 'Invalid email or password');
        }
      } else {
        const cleanName = form.name.trim() || cleanEmail.split('@')[0];
        const result = await signup(cleanEmail, cleanPassword, cleanName);
        if (result.success) {
          setSuccessMsg('Account created successfully! If email verification is enabled, please check your inbox.');
          setTimeout(() => {
            setIsLogin(true);
            setSuccessMsg('Please sign in with your credentials.');
          }, 3000);
        } else {
          setError(result.error || 'Failed to create account');
        }
      }
    } catch (err: any) {
      setError('An unexpected system error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequest = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMsg('');

    const cleanEmail = form.email.trim().toLowerCase();
    if (!cleanEmail) {
      setError('Please enter your network address first.');
      setLoading(false);
      return;
    }

    try {
      const result = await resetPasswordForEmail(cleanEmail);
      if (result.success) {
        setSuccessMsg(`A password reset link has been dispatched to ${cleanEmail}. Please check your inbox and spam folder.`);
      } else {
        setError(result.error || 'Failed to dispatch reset link.');
      }
    } catch (err: any) {
      setError(err?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMsg('');

    if (newPassword.length < 6) {
      setError('Access key must be at least 6 characters.');
      setLoading(false);
      return;
    }

    try {
      const result = await updatePassword(newPassword);
      if (result.success) {
        // Clean any recovery hash fragments from browser URL
        try {
          window.history.replaceState(null, '', window.location.pathname);
        } catch (e) {}

        // Ensure clean session state so user can log in with new key
        await logout();
        
        setSuccessMsg('Access key successfully updated! Please sign in with your new credentials.');
        setNewPassword('');
        setIsRecoveryMode(false);
        setIsLogin(true);
      } else {
        setError(result.error || 'Failed to update access key.');
      }
    } catch (err: any) {
      setError(err?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isCredentialError = error.toLowerCase().includes('invalid') || error.toLowerCase().includes('credential');

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 flex-col">
      <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="text-center mb-8 flex flex-col items-center">
          <WealthyLogo size={36} className="mb-4" />
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Wealthy</h1>
          <p className="text-slate-500 mt-2 font-medium">Precision finance for the modern world</p>
        </div>

        <div className="bg-white rounded-[40px] shadow-2xl shadow-slate-200/60 p-8 sm:p-10 border border-slate-100 overflow-hidden relative">
          {isRecoveryMode ? (
            /* Scenario 1: Password Recovery Mode */
            <form onSubmit={handleUpdatePassword} className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <KeyRound size={24} />
                </div>
                <h2 className="text-xl font-black text-slate-900 uppercase tracking-wider">Update Access Key</h2>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Configure a new secure login password</p>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">New Access Key</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300"><Lock size={18} /></span>
                  <input 
                    required 
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="At least 6 characters"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    className="w-full pl-12 pr-12 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-blue-600 outline-none transition-all" 
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="p-4 bg-rose-50 rounded-2xl text-rose-600 animate-in slide-in-from-top-2 border border-rose-100">
                  <div className="flex items-center gap-2">
                    <AlertCircle size={16} className="flex-shrink-0" />
                    <p className="text-xs font-bold leading-tight">{error}</p>
                  </div>
                </div>
              )}

              {successMsg && (
                <div className="p-4 bg-teal-50 rounded-2xl text-teal-600 animate-in slide-in-from-top-2 border border-teal-100">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="flex-shrink-0" />
                    <p className="text-xs font-bold leading-tight">{successMsg}</p>
                  </div>
                </div>
              )}

              <button 
                disabled={loading}
                className="w-full bg-slate-900 text-white font-black py-4.5 rounded-[24px] flex items-center justify-center gap-3 hover:bg-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed group shadow-xl shadow-slate-200 uppercase tracking-widest text-[11px]"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : (
                  <>
                    Save New Access Key
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  try {
                    window.history.replaceState(null, '', window.location.pathname);
                  } catch (e) {}
                  setIsRecoveryMode(false);
                  setIsLogin(true);
                  setError('');
                  setSuccessMsg('');
                }}
                className="w-full text-center text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-600 transition-colors pt-2 flex items-center justify-center gap-2"
              >
                <ArrowLeft size={12} /> Return to Sign In
              </button>
            </form>
          ) : isForgotMode ? (
            /* Scenario 2: Forgot Password Mode */
            <form onSubmit={handleForgotRequest} className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <KeyRound size={24} />
                </div>
                <h2 className="text-xl font-black text-slate-900 uppercase tracking-wider">Reset Access Key</h2>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">We'll dispatch a secure recovery link to your email</p>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Network Address (Email)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300"><Mail size={18} /></span>
                  <input 
                    required 
                    type="email" 
                    placeholder="name@email.com"
                    value={form.email}
                    onChange={e => setForm({...form, email: e.target.value})}
                    className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-blue-600 outline-none transition-all" 
                  />
                </div>
              </div>

              {error && (
                <div className="p-4 bg-rose-50 rounded-2xl text-rose-600 animate-in slide-in-from-top-2 border border-rose-100">
                  <div className="flex items-center gap-2">
                    <AlertCircle size={16} className="flex-shrink-0" />
                    <p className="text-xs font-bold leading-tight">{error}</p>
                  </div>
                </div>
              )}

              {successMsg && (
                <div className="p-4 bg-teal-50 rounded-2xl text-teal-600 animate-in slide-in-from-top-2 border border-teal-100">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" />
                    <p className="text-xs font-bold leading-relaxed">{successMsg}</p>
                  </div>
                </div>
              )}

              <button 
                disabled={loading}
                className="w-full bg-slate-900 text-white font-black py-4.5 rounded-[24px] flex items-center justify-center gap-3 hover:bg-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed group shadow-xl shadow-slate-200 uppercase tracking-widest text-[11px]"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : (
                  <>
                    Send Recovery Link
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setIsForgotMode(false);
                  setError('');
                  setSuccessMsg('');
                }}
                className="w-full text-center text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-600 transition-colors pt-2 flex items-center justify-center gap-2"
              >
                <ArrowLeft size={12} /> Back to Sign In
              </button>
            </form>
          ) : (
            /* Scenario 3: Standard Login / Registration */
            <>
              <div className="flex p-1 bg-slate-100 rounded-2xl mb-8">
                <button 
                  onClick={() => { setIsLogin(true); setError(''); setSuccessMsg(''); }}
                  className={`flex-1 py-3 text-xs font-black rounded-xl transition-all uppercase tracking-widest ${isLogin ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  Sign In
                </button>
                <button 
                  onClick={() => { setIsLogin(false); setError(''); setSuccessMsg(''); }}
                  className={`flex-1 py-3 text-xs font-black rounded-xl transition-all uppercase tracking-widest ${!isLogin ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  Register
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {!isLogin && (
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Identity (Full Name)</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300"><User size={18} /></span>
                      <input 
                        required 
                        type="text" 
                        placeholder="Your Name"
                        value={form.name}
                        onChange={e => setForm({...form, name: e.target.value})}
                        className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-blue-600 outline-none transition-all" 
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Network Address (Email)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300"><Mail size={18} /></span>
                    <input 
                      required 
                      type="email" 
                      placeholder="name@email.com"
                      value={form.email}
                      onChange={e => setForm({...form, email: e.target.value})}
                      className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-blue-600 outline-none transition-all" 
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center ml-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Access Key (Password)</label>
                    {isLogin && (
                      <button
                        type="button"
                        onClick={() => {
                          setIsForgotMode(true);
                          setError('');
                          setSuccessMsg('');
                        }}
                        className="text-[10px] font-black text-blue-600 hover:text-blue-700 uppercase tracking-widest transition-colors"
                      >
                        Forgot Key?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300"><Lock size={18} /></span>
                    <input 
                      required 
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={form.password}
                      onChange={e => setForm({...form, password: e.target.value})}
                      className="w-full pl-12 pr-12 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-blue-600 outline-none transition-all" 
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                      title={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="p-4 bg-rose-50 rounded-2xl text-rose-600 animate-in slide-in-from-top-2 border border-rose-100 space-y-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                      <p className="text-xs font-bold leading-snug">{error}</p>
                    </div>

                    {/* Helpful actions when credential error occurs */}
                    {isCredentialError && isLogin && (
                      <div className="pt-2 border-t border-rose-100 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setIsForgotMode(true);
                            setError('');
                            setSuccessMsg('');
                          }}
                          className="px-3 py-1.5 bg-white border border-rose-200 text-rose-700 rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-rose-100/50 transition-all flex items-center gap-1.5 shadow-sm active:scale-95"
                        >
                          <KeyRound size={12} /> Reset Password
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsLogin(false);
                            setError('');
                            setSuccessMsg('');
                          }}
                          className="px-3 py-1.5 bg-rose-600 text-white rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-rose-700 transition-all flex items-center gap-1.5 shadow-sm active:scale-95"
                        >
                          <Sparkles size={12} /> Register Account
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {successMsg && (
                  <div className="p-4 bg-teal-50 rounded-2xl text-teal-600 animate-in slide-in-from-top-2 border border-teal-100">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={16} className="flex-shrink-0" />
                      <p className="text-xs font-bold leading-tight">{successMsg}</p>
                    </div>
                  </div>
                )}

                <button 
                  disabled={loading}
                  className="w-full bg-slate-900 text-white font-black py-4.5 rounded-[24px] flex items-center justify-center gap-3 hover:bg-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed group shadow-xl shadow-slate-200 uppercase tracking-widest text-[11px]"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : (
                    <>
                      {isLogin ? 'Establish Session' : 'Create Credentials'}
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          <p className="mt-8 text-center text-[10px] text-slate-400 font-bold uppercase tracking-[0.15em]">
            Precision Financial Systems &copy; 2025
          </p>
        </div>
      </div>
      
      <p className="mt-12 text-[10px] font-black text-slate-400 uppercase tracking-widest opacity-50">
        Engineered by Alessandro Calanni
      </p>
    </div>
  );
};

export default Login;
